"use client";

import * as React from "react";
import { TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Clock, Droplets, Flame, PhoneCall, Cylinder as Pipe, ShieldCheck, Sparkles, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const signs = [{
  title: "Frequent Pipe Leaks",
  description: "Repeated pinhole leaks or burst lines in multiple rooms indicate systemic pipe wall deterioration rather than isolated faults.",
  icon: Droplets
}, {
  title: "Discolored or Rusty Water",
  description: "Brown, yellow, or reddish tap water suggests internal pipe corrosion and scale flaking into your household water supply.",
  icon: AlertTriangle
}, {
  title: "Low Water Pressure",
  description: "Mineral buildup inside old galvanized steel lines restricts volume across all faucets and showers simultaneously.",
  icon: Flame
}, {
  title: "Aging Pipe Materials",
  description: "Homes built over 40 years ago with galvanized iron, polybutylene, or deteriorating lead lines require full modern replacement.",
  icon: Clock
}];
const benefits = [{
  title: "Restored Full Water Pressure",
  description: "Smooth modern PEX or high-grade copper piping eliminates friction loss and delivers crisp, strong flow to every fixture."
}, {
  title: "Clean, Safe Drinking Water",
  description: "Say goodbye to metallic tastes, rust particles, and sediment with non-corroding, certified potable water systems."
}, {
  title: "Prevention of Costly Water Damage",
  description: "Stop paying for repeated emergency slab leak repairs and avoid catastrophic ceiling, drywall, or foundation flooding."
}, {
  title: "Higher Home Resale Value",
  description: "A certified new repiping system gives future buyers total confidence and passes home inspections with flying colors."
}];
export default function RepipingOverview() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12,
        delayChildren: prefersReducedMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="repipingoverview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs uppercase tracking-wider font-semibold">
            Comprehensive Plumbing Solutions
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Whole-Home Repiping Services in Jonesboro
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            When patching temporary leaks is no longer cost-effective, a complete system repipe replaces failing, rusted, or outdated plumbing with durable PEX or copper solutions designed to last decades.
          </p>
        </div>

        {/* Feature Split Hero / Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-20">
          <div className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
              <Pipe className="size-5 text-accent" />
              <span>Permanent Protection For Your Property</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
              What Is Whole-House Repiping?
            </h3>
            <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
              Whole-home repiping is the complete replacement of your incoming supply lines and distribution network. Our licensed Jonesboro plumbers carefully route modern, flexible PEX or premium copper lines throughout your walls and crawlspaces with minimal invasive disruption to your living space.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-card border border-border">
                <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm text-card-foreground">Code-Compliant</h4>
                  <p className="text-xs text-muted-foreground">Fully permitted and inspected work</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-card border border-border">
                <Sparkles className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm text-card-foreground">Modern Materials</h4>
                  <p className="text-xs text-muted-foreground">Non-toxic, scale-resistant piping</p>
                </div>
              </div>
            </div>
            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="font-semibold shadow-sm">
                <Link to="/contact/">Schedule Repiping Inspection</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href="tel:4708479076" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4 text-primary" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-lg bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumbing_repiping_inspection.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumbing_repiping_inspection.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumbing_repiping_inspection.png-wvc-sizes" alt="Plumber inspecting domestic water supply line inside a home" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-transparent to-transparent flex items-end p-6">
                <div className="text-background">
                  <span className="text-xs uppercase font-bold tracking-widest text-accent">Professional Assessment</span>
                  <p className="text-sm sm:text-base font-semibold mt-1">
                    Free on-site pipe evaluation &amp; upfront pricing across Jonesboro, GA
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-12" />

        {/* When It's Needed Section */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="outline" className="mb-3 text-xs uppercase font-medium">
              Diagnostics &amp; Warning Signs
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground mb-3">
              When Is Repiping Necessary?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              Look out for these clear indicators that your home water delivery system is nearing the end of its useful lifespan.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {signs.map((sign, i) => {
            const IconComponent = sign.icon;
            return <motion.div key={sign.title} variants={itemVariants} data-index={i}>
                  <Card className="h-full border-border bg-card shadow-xs hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-2">
                        <IconComponent className="size-5" />
                      </div>
                      <CardTitle className="text-lg font-bold text-card-foreground">
                        {sign.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {sign.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Benefits Section */}
        <div className="rounded-2xl bg-secondary/50 border border-border p-6 sm:p-10 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5 space-y-4">
              <Badge variant="default" className="text-xs uppercase">
                Long-Term Value
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
                Key Benefits of a Modern Repipe
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Investing in a modern pipe system brings immediate daily relief and guards your property from catastrophic water loss for the next 40+ years.
              </p>
              <div className="pt-2">
                <Button asChild variant="default" className="font-semibold shadow-sm">
                  <Link to="/services/">Explore All Plumbing Services</Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {benefits.map((benefit, i) => <div key={benefit.title} data-index={i} className="p-5 rounded-xl bg-card border border-border shadow-xs flex flex-col justify-start space-y-2">
                  <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span className="text-card-foreground">{benefit.title}</span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* Quick Consultation Banner */}
        <div className="mt-16 text-center bg-card border border-border rounded-xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-left">
            <h4 className="text-lg font-bold text-card-foreground">Need an Emergency Assessment in Jonesboro?</h4>
            <p className="text-sm text-muted-foreground">Our 24/7 plumbers are ready to evaluate your pipe system immediately.</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button asChild size="default">
              <Link to="/contact/">Request Free Quote</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}